import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import showToast from '../utils/toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const toErrorMessage = (value: unknown, fallback = ''): string => {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (typeof obj.message === 'string' && obj.message.trim()) return obj.message;
    if (typeof obj.error === 'string' && obj.error.trim()) return obj.error;
    if (obj.error && typeof obj.error === 'object') {
      const nested = obj.error as Record<string, unknown>;
      if (typeof nested.message === 'string' && nested.message.trim()) return nested.message;
    }
  }
  return fallback;
};

// Helper to extract error message from API response
const getErrorMessage = (error: AxiosError): string => {
  if (error.response?.data) {
    const data = error.response.data as any;
    return (
      toErrorMessage(data.message)
      || toErrorMessage(data.error)
      || 'An error occurred'
    );
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

// Helper to extract success message from API response
const getSuccessMessage = (response: AxiosResponse, endpoint: string): string => {
  const data = response.data as any;
  
  // Only return message if operation was actually successful
  if (data.success !== true) {
    return '';
  }
  
  // Use explicit message from response if available
  if (data.message) {
    return data.message;
  }

  // Generate contextual messages based on endpoint
  if (endpoint.includes('/auth/login')) {
    return 'Login successful!';
  }
  if (endpoint.includes('/auth/register')) {
    return 'Account created successfully!';
  }
  if (endpoint.includes('/copy-trading/enable')) {
    return 'Copy trading enabled!';
  }
  if (endpoint.includes('/copy-trading/disable')) {
    return 'Copy trading disabled!';
  }
  if (endpoint.includes('/copy-trading/allocation')) {
    return 'Allocation updated!';
  }
  if (endpoint.includes('/admin/approve-master')) {
    return 'Master trader approved!';
  }
  if (endpoint.includes('/admin/reject-master')) {
    return 'Master trader rejected!';
  }

  return '';
};

// List of endpoints that should not show success toasts
// (Components handle their own toasts for these endpoints)
const silentEndpoints = [
  '/auth/me',
  '/auth/option-rm',
  '/auth/genesis-rm',
  '/dashboard',
  '/trades',
  '/dashboard/masters',
  '/subscriptions/create-checkout',
  '/subscriptions/resume',
  '/subscriptions/cancel',
  '/subscriptions/change-plan',
  '/admin/pending-masters',
  '/admin/user/', // Component handles toast (user deletion, terms template deletion)
  '/admin/subscriptions/bulk-update', // Component handles toast
  '/admin/subscriptions/update-single', // Component handles toast
  '/admin/subscriptions/extend', // Component handles toast
  '/comments', // Component handles toast
  '/signals', // Component handles toast (for delete)
  '/brokerage/supported', // Component handles toast
  '/brokerage/connections', // Component handles toast
  '/brokerage/connect', // Component handles toast
  '/brokerage/account', // Component handles toast
  '/brokerage/positions', // Component handles toast
  '/brokerage/orders', // Component handles toast
  '/brokerage/webull-oauth', // Webull Connect tab / callback logs payloads itself
];

// Check if endpoint should be silent
const shouldShowSuccessToast = (endpoint: string, method: string): boolean => {
  if (method.toLowerCase() === 'get') {
    return false;
  }
  return !silentEndpoints.some(silent => endpoint.includes(silent));
};

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token to requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle toasts and errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const endpoint = response.config.url || '';
    const method = response.config.method || 'get';
    const data = response.data as any;
    
    // Only show success toast if operation actually succeeded
    if (shouldShowSuccessToast(endpoint, method)) {
      // Strictly check for success === true
      if (data.success === true) {
        const message = getSuccessMessage(response, endpoint);
        if (message) {
          showToast('success', message);
        }
      } else if (data.success === false || data.error) {
        showToast(
          'error',
          toErrorMessage(data.message) || toErrorMessage(data.error) || 'Operation failed'
        );
      }
    }
    
    return response;
  },
  (error: AxiosError) => {
    const endpoint = error.config?.url || '';
    const status = error.response?.status;
    const errorData = error.response?.data as any;
    
    // Don't redirect on 401 if it's a login/register request (user is already on auth page)
    const isAuthRequest = endpoint.includes('/auth/login') || endpoint.includes('/auth/register');
    const isAuthMeRequest = endpoint.includes('/auth/me');
    
    // Only handle 401 for non-auth endpoints
    if (status === 401 && !isAuthRequest) {
      // Check if this is a real authentication failure (not a network error or temporary issue)
      const isNetworkError = !error.response; // No response means network error
      const isTokenExpired = errorData?.error?.toLowerCase().includes('expired') || 
                            errorData?.error?.toLowerCase().includes('invalid token') ||
                            errorData?.error?.toLowerCase().includes('not authorized');
      
      // Only logout if:
      // 1. It's a confirmed token expiration/invalid token error (not network error)
      // 2. OR it's an /auth/me request (which means token is definitely invalid)
      // 3. AND we're not already on the login page
      if ((isTokenExpired || isAuthMeRequest) && !isNetworkError && window.location.pathname !== '/login') {
        // Clear token only on confirmed auth failures
        localStorage.removeItem('token');
        // Use navigate instead of window.location to avoid full page reload
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      // For network errors or temporary 401s, don't logout - let the request fail gracefully
    } else if (!isAuthRequest) {
      // Show error toast for all other errors (but not for login/register - they handle their own errors)
      const errorMessage = getErrorMessage(error);
      showToast('error', errorMessage);
    }
    // For auth requests, don't show toast - let the component handle error display
    
    return Promise.reject(error);
  }
);

export default apiClient;

